/**
 * /coach-meeting — Parent books a 15-min monthly coaches check-in.
 *
 * Mon–Thu · 8:30 AM, 8:45 AM, 3:30 PM, 3:45 PM Pacific · rolling 2 weeks.
 * One click → form → confirmation card with Jitsi link.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Clock, MapPin, Check, Loader2, ArrowLeft, Sun, Moon } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const TAG = 'text-[10px] font-black uppercase tracking-[0.28em]';

const Field = ({ label, children, required }) => (
  <div>
    <label className={TAG + ' text-slate-500 mb-2 block'}>
      {label}{required && <span className="text-amber-700"> *</span>}
    </label>
    {children}
  </div>
);

const inputCls = 'w-full bg-white border border-slate-300 focus:border-amber-700 focus:ring-1 focus:ring-amber-700 outline-none px-3 py-2.5 text-sm text-slate-900 rounded';

export default function CoachMeeting() {
  const [window, setWindow] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [parentName, setParentName] = useState('');
  const [parentEmail, setParentEmail] = useState('');
  const [studentName, setStudentName] = useState('');
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/coach-meeting/availability');
      setWindow(data.window || []);
    } catch {
      toast.error('Could not load availability');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openDay = useMemo(
    () => window.find((d) => d.date === selectedDate) || null,
    [window, selectedDate]
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedDate || !selectedSlot) return toast.error('Pick a date and time first');
    if (!parentName.trim() || !parentEmail.trim() || !studentName.trim()) return toast.error('Please fill in your name, email, and your scholar\'s name');
    setSubmitting(true);
    try {
      const { data } = await api.post('/coach-meeting/book', {
        parent_name: parentName.trim(),
        parent_email: parentEmail.trim(),
        student_name: studentName.trim(),
        phone: phone.trim() || null,
        note: note.trim() || null,
        date: selectedDate,
        time: selectedSlot,
      });
      setConfirmed(data.booking);
      toast.success('Booked — check your inbox for the video link');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not complete the booking');
      // Reload availability in case the slot was just taken.
      await load();
      setSelectedSlot(null);
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmed) {
    return (
      <div className="min-h-screen bg-[#F4F1EA] text-slate-900">
        <div className="max-w-xl mx-auto px-5 py-14">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-md border border-slate-200 p-8 text-center shadow-sm"
            data-testid="coach-meeting-confirmed"
          >
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/10 border-2 border-emerald-500 text-emerald-600 flex items-center justify-center mb-5">
              <Check className="w-8 h-8" />
            </div>
            <div className={TAG + ' text-amber-700 mb-2'}>You're on the books</div>
            <h1 className="text-2xl font-black mb-3">Coach meeting confirmed</h1>
            <div className="text-sm text-slate-600 mb-5">
              {confirmed.day_label} · <span className="text-slate-900 font-bold">{confirmed.slot_label} PT</span>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded p-4 mb-5 text-left">
              <div className={TAG + ' text-slate-500 mb-1'}>In person at</div>
              <div className="text-sm font-bold text-slate-900">Scholar Athlete Academy</div>
              <div className="text-sm text-slate-600">20611 Prairie St</div>
            </div>
            <p className="text-xs text-slate-500 mb-4">
              <strong>Can't make it in person?</strong> Reply to the confirmation email we just sent to <strong>{confirmed.parent_email}</strong> and we'll send a video link for the same slot.
            </p>
            <button
              onClick={() => {
                setConfirmed(null); setSelectedDate(null); setSelectedSlot(null);
                setParentName(''); setParentEmail(''); setStudentName(''); setPhone(''); setNote('');
                load();
              }}
              className="mt-6 text-xs uppercase tracking-widest font-black text-amber-700 hover:text-amber-900"
              data-testid="coach-meeting-another"
            >
              Book another slot
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F1EA] text-slate-900">
      <div className="max-w-3xl mx-auto px-5 py-10">
        <div className={TAG + ' text-amber-700 mb-2'}>Scholar Athlete Academy</div>
        <h1 className="text-3xl sm:text-4xl font-black leading-tight mb-3">
          New month · let's talk about your scholar
        </h1>
        <p className="text-slate-600 text-base leading-relaxed mb-8 max-w-2xl">
          As we begin a new month, we'd like to sit down with you <strong>in person at the academy</strong> for
          a quick 10&ndash;15 minute coaches check-in so we're all aligned on how your scholar is doing &mdash;
          academically, athletically, and otherwise &mdash; and what we're focused on for the weeks ahead.
          Pick the time that works for you below. If you can't make it in person, book the slot that works
          and reply to the confirmation &mdash; we'll send a video link for that same time.
        </p>

        <div className="flex flex-wrap gap-3 mb-6 text-xs text-slate-600">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded"><Calendar className="w-3 h-3" /> Mon–Thu</span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded"><Sun className="w-3 h-3" /> 8:30 / 8:45 AM</span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded"><Moon className="w-3 h-3" /> 3:30 / 3:45 PM</span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded"><Clock className="w-3 h-3" /> 15 min · Pacific</span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded"><MapPin className="w-3 h-3" /> In person · 20611 Prairie St</span>
        </div>

        {loading ? (
          <div className="py-20 flex items-center justify-center text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : (
          <>
            {!selectedDate && (
              <div className="space-y-3" data-testid="coach-meeting-day-list">
                <div className={TAG + ' text-slate-500 mb-2'}>Pick a day</div>
                {window.length === 0 && (
                  <div className="text-sm text-slate-500 bg-white border border-slate-200 rounded p-5">
                    No open slots in the next two weeks. Please check back soon.
                  </div>
                )}
                {window.map((d) => {
                  const open = d.slots.filter((s) => !s.is_booked).length;
                  return (
                    <button
                      key={d.date}
                      onClick={() => setSelectedDate(d.date)}
                      disabled={open === 0}
                      className={`w-full text-left bg-white border rounded p-4 flex items-center justify-between transition
                        ${open === 0 ? 'border-slate-200 opacity-50 cursor-not-allowed' : 'border-slate-200 hover:border-amber-700 hover:shadow-sm'}`}
                      data-testid={`coach-day-${d.date}`}
                    >
                      <div>
                        <div className="font-bold text-slate-900">{d.day_label}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{open} slot{open === 1 ? '' : 's'} open</div>
                      </div>
                      <ArrowLeft className="w-4 h-4 rotate-180 text-slate-400" />
                    </button>
                  );
                })}
              </div>
            )}

            <AnimatePresence>
              {selectedDate && openDay && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                >
                  <button
                    onClick={() => { setSelectedDate(null); setSelectedSlot(null); }}
                    className="text-xs uppercase tracking-widest font-black text-amber-700 hover:text-amber-900 inline-flex items-center gap-1 mb-4"
                    data-testid="coach-back"
                  >
                    <ArrowLeft className="w-3 h-3" /> Back to all days
                  </button>

                  <div className={TAG + ' text-slate-500 mb-2'}>{openDay.day_label}</div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
                    {openDay.slots.map((s) => (
                      <button
                        key={s.time}
                        type="button"
                        onClick={() => !s.is_booked && setSelectedSlot(s.time)}
                        disabled={s.is_booked}
                        className={`py-3 rounded border-2 font-black text-sm transition
                          ${s.is_booked ? 'bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed line-through' :
                            selectedSlot === s.time ? 'bg-[#1B3B2B] border-[#1B3B2B] text-white' :
                            'bg-white border-slate-200 text-slate-900 hover:border-amber-700'}`}
                        data-testid={`coach-slot-${s.time}`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>

                  {selectedSlot && (
                    <motion.form
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                      onSubmit={submit}
                      className="bg-white border border-slate-200 rounded p-5 space-y-4"
                      data-testid="coach-form"
                    >
                      <Field label="Your name" required>
                        <input className={inputCls} value={parentName} onChange={(e) => setParentName(e.target.value)} placeholder="First and last" data-testid="coach-parent-name" />
                      </Field>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Your email" required>
                          <input type="email" className={inputCls} value={parentEmail} onChange={(e) => setParentEmail(e.target.value)} placeholder="you@example.com" data-testid="coach-parent-email" />
                        </Field>
                        <Field label="Phone (optional)">
                          <input type="tel" className={inputCls} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="661-555-0142" data-testid="coach-phone" />
                        </Field>
                      </div>
                      <Field label="Your scholar's name" required>
                        <input className={inputCls} value={studentName} onChange={(e) => setStudentName(e.target.value)} placeholder="e.g. Vance Johnson" data-testid="coach-student-name" />
                      </Field>
                      <Field label="Anything you'd like to make sure we cover? (optional)">
                        <textarea className={inputCls + ' resize-none'} rows={3} value={note} onChange={(e) => setNote(e.target.value)} data-testid="coach-note" />
                      </Field>

                      <button
                        type="submit"
                        disabled={submitting}
                        className="w-full bg-[#1B3B2B] hover:bg-[#143021] disabled:opacity-60 text-white font-black uppercase tracking-widest text-sm py-3.5 rounded flex items-center justify-center gap-2"
                        data-testid="coach-submit"
                      >
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Confirm {openDay.day_label.split(',')[0]} at {openDay.slots.find((s) => s.time === selectedSlot)?.label} &rarr;</>}
                      </button>
                    </motion.form>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}
      </div>
    </div>
  );
}
